import config from "@/config/config";
import { openApi } from "@/config/httpClient";
import { BlacklistEntry, BookingResult, CreateHoliday, CreateRoom, CreateTimeSlotDefinition, Holiday, ResultTicket, ResultTimeSlot, Room, Ticket, TicketOwner, TimeSlot, TimeSlotCapacityUpdate, TimeSlotCreationResult, UpdateTimeSlot } from "@/generated/openapi";
import { asTicketOwner, PersonalData } from "@/models/PersonalData";
import { SortOptions } from "@/models/SortOptions";
import { asSortString } from "@/utils/apiUtils";
import { now } from "@/utils/dateTimeUtils";

class AdminService {

  public getAllRooms() {
    return openApi.findRoomsByAdmin(undefined, undefined, config.maxRooms, 0)
      .then(resp => resp.data.content || [])
  }
  public getRoom(id: string) {
    return openApi.getRoomByAdmin(id)
      .then(resp => resp.data)
  }
  public orderRooms(id: string, index: number) {
    return openApi.orderRoom(id,index)
      .then(resp => resp.data)
  }
  public createRoom(createRoom: CreateRoom) {
    return openApi.createRoomByAdmin(createRoom)
  }
  public updateRoom(updateRoom: Room) {
    return openApi.updateRoomByAdmin(updateRoom.id, updateRoom).then(resp => resp.data)
  }
  public deleteRoom(id: string) {
    return openApi.deleteRoomByAdmin(id)
  }

  public uploadImage(roomId: string, alt: string, file: File) {
    return openApi.uploadRoomImageByAdmin(roomId, file, alt)
  }

  public deleteImage(roomId: string) {
    return openApi.removeRoomImageByAdmin(roomId)
  }

  public findTimeSlots(roomId: string, fromDate: string|undefined, toDate: string|undefined): Promise<ResultTimeSlot> {
    return openApi.findTimeslotsByAdmin(roomId, fromDate, toDate, undefined, config.maxTimeSlots, 0).then(resp => resp.data)
  }

  public findOverbookedTimeSlots(): Promise<TimeSlot[]> {
    return openApi.findAllTimeslotsByAdmin(now().toISODate(), undefined, 'OVERBOOKED', config.maxTimeSlots, 0, 'date,startTime')
      .then(resp => resp.data.content || [])
  }

  public getTimeSlot(roomId: string, timeSlotId: string) {
    return openApi.getTimeSlotByAdmin(roomId, timeSlotId).then(resp => resp.data)
  }
  public createTimeSlot(roomId: string, createTimeSlot: CreateTimeSlotDefinition): Promise<TimeSlotCreationResult> {
    return openApi.createTimeSlotsByAdmin(roomId, createTimeSlot).then(resp => resp.data)
  }
  public updateTimeSlot(roomId: string, timeSlotId: string, timeSlot: UpdateTimeSlot) {
    return openApi.updateTimeSlotByAdmin(roomId, timeSlotId, timeSlot).then(resp => resp.data)
  }
  public updateMultiCapacity(roomId: string, timeslotCapacityUpdate: TimeSlotCapacityUpdate) {
    return openApi.updateTimeSlotsCapacityByAdmin(roomId, timeslotCapacityUpdate).then(resp => resp.data)
  }
  public deleteTimeSlot(roomId: string, timeSlotId: string) {
    return openApi.deleteTimeSlotByAdmin(roomId, timeSlotId)
  }

  public findTicketsByDate(offset: number, sortOptions: SortOptions, statusFilter?: 'INCLUDE_CANCELED'|'CANCELED_ONLY', searchTerm?: string, fromDate?: string, untilDate?: string): Promise<ResultTicket> {
    return openApi.findTicketsByAdmin(searchTerm, statusFilter, undefined, undefined, undefined, undefined, undefined, undefined, fromDate, untilDate, undefined, undefined, config.pagingSize, offset, asSortString(sortOptions))
      .then(resp => resp.data)
  }

  public findDailyTickets(roomId: string, date: string): Promise<ResultTicket> {
    return openApi.findTicketsByAdmin(undefined, undefined, roomId, undefined, undefined, undefined, undefined, undefined, date, date, undefined, undefined, config.maxDailyTickets, 0, 'startTime,lastName')
      .then(resp => resp.data)
  }

  public findTicketsByTimeSlot(timeSlotId: string): Promise<ResultTicket> {
    return openApi.findTicketsByAdmin(undefined, undefined, undefined, timeSlotId).then(resp => resp.data)
  }

  public relatedTickets(ownerId: string, excludedTicketId: string): Promise<Ticket[]> {
    return openApi.findTicketsByAdmin(undefined, undefined, undefined, undefined, undefined, ownerId)
      .then(resp => resp.data.content || [])
      .then(tickets => tickets.filter(ticket => ticket.id !== excludedTicketId))
  }

  public updateTicket(ticketId: string, ticketOwner: TicketOwner) {
    return openApi.updateTicketOwnerByAdmin(ticketId, ticketOwner);
  }

  public countActiveTickets(roomId: string): Promise<number> {
    return openApi.findTicketsByAdmin(undefined, undefined, roomId, undefined, undefined, undefined, undefined, undefined, now().toISODate(), undefined, undefined, undefined, config.maxReservations, 0, undefined)
      .then(resp => resp.data.totalCount || 0)
  }

  public getTicket(id: string) {
    return openApi.getTicketByAdmin(id)
  }

  public cancelTicket(id: string[]) {
    return openApi.deleteTicketsByAdmin(id.join(','))
  }

  public bookTicket(timeSlotId: string, personalData: PersonalData): Promise<BookingResult> {
    return new Promise((resolve, reject) => {
      openApi.createTransaction({
        timeSlotId
      }).then(resp => {
        if (typeof resp.data.transactionId === 'undefined') {
          throw new Error();
        }
        return openApi.bookTransaction(resp.data.transactionId, asTicketOwner(personalData))
      }).then(resp => {
        resolve(resp.data)
      }).catch(() => {
        reject()
      })
    })
  }

  public getHolidays(year: number) {
    return openApi.findHolidaysByAdmin('', `${year}-01-01`, `${year}-12-31`, 999)
      .then(resp => resp.data)
  }

  public deleteHoliday(id: string) {
    return openApi.deleteHolidayByAdmin(id)
  }

  public addHoliday(createHoliday: CreateHoliday) {
    return openApi.createHolidayByAdmin(createHoliday)
  }

  public getHoliday(id: string) {
    return openApi.getHolidayByAdmin(id)
      .then(resp => resp.data);
  }

  public updateHoliday(holiday: Holiday) {
    return openApi.updateHoliday(holiday.id, holiday)
  }

  public importHolidayIcal(file: File) {
    return openApi.uploadHolidayICalByAdmin(file)
  }


  public getBlackList(searchTerm: string|undefined, sortBy: string, sortOrder: string): Promise<BlacklistEntry[]> {
    return openApi.findBlacklistEntriesByAdmin(searchTerm, undefined, undefined, config.maxBlackListEntries, 0, sortBy + ':' + sortOrder)
      .then(resp => resp.data.content || [])
  }

  public getBlacklistEntry(email: string): Promise<BlacklistEntry> {
    return openApi.getBlacklistEntryByAdmin(email)
      .then(resp => resp.data)
  }

  public createBlacklistEntry(blacklistEntry: BlacklistEntry): Promise<BlacklistEntry> {
    return openApi.addEmailToBlacklistByAdmin(blacklistEntry)
      .then(resp => resp.data)
  }

  public updateBlacklistEntry(email: string, blacklistEntry: BlacklistEntry): Promise<BlacklistEntry> {
    return openApi.removeEmailFromBlacklist(email).then(() => {
      return openApi.addEmailToBlacklistByAdmin(blacklistEntry)
    }).then(resp => resp.data)
  }

  public deleteBlacklistEntry(email: string): Promise<void> {
    return openApi.removeEmailFromBlacklist(email).then(resp => resp.data)
  }

}

const adminService = new AdminService();

export {
  adminService
};
